import { Property } from 'csstype'

import { BreakpointType } from '@thg-commerce/gravity-theme/breakpoints'

import { TextStyling, Font, HeaderSlotTheme } from '../'

import {
  Order,
  Alignment,
  Border,
  BorderBox,
  Separator,
  Visibility,
  Orientation,
} from './types'

export enum HeaderComponent {
  LOGO = 'logo',
  NAVIGATION = 'navigation',
  SEARCH = 'search',
  ACCOUNT = 'account',
  BASKET = 'basket',
  WISHLIST = 'wishlist',
  FLYOUT = 'flyout',
  SUBMENU = 'submenu',
}

export enum DropdownDirection {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

export enum FlyoutDirection {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

export type SlotConfiguration = {
  component: HeaderComponent
  colSpan: string
  alignment: Alignment
}

export type IconStyling = {
  svgPath: string
  height: string
  width: string
  color: Property.Color
  viewBox: string
}

export interface HeaderThemeInterface {
  backgroundColor: Property.Color
  bottomSeparator: Separator
  slotsGridAreas: BreakpointType<{ area: string; colSpan: string }>
  accountDropdownDirection: BreakpointType<DropdownDirection>
  enabledComponents: BreakpointType<HeaderComponent[]>
  slots: BreakpointType<SlotConfiguration[]>[]
  flyout: {
    direction: BreakpointType<string>
    icon: IconStyling
  }
  headerSlots: {
    iconColor: {
      active: Property.Color
      inactive: Property.Color
    }
    textStyling: {
      active: TextStyling
      inactive: TextStyling
    }
    orientation: Orientation
  }
  slotItems: {
    submenu: HeaderSlotTheme[]
    header: HeaderSlotTheme[]
    flyout: HeaderSlotTheme[]
  }
  dropdowns: {
    order: {
      mobile: Order
      desktop: Order
    }
    triggerHeight: string
    account: {
      hideText: boolean
      width: string
      icon: IconStyling
      iconColor: Property.Color
      closeIconColor: Property.Color
      hoverIconColor: Property.Color
      mobileIconColor: Property.Color
      mobileIcon: IconStyling
      font: {
        trigger: TextStyling
        dropdownLink: TextStyling
        ctaHigh: Font
        ctaMedium: Font
        welcomeText: TextStyling
      }
    }
    basket: {
      hideText: boolean
      width: string
      icon: IconStyling
      mobileIcon: IconStyling
      iconColor: Property.Color
      closeIconColor: Property.Color
      hoverIconColor: Property.Color
      mobileIconColor: Property.Color
      quantityStyling: {
        height: string
        width: string
        mobileHeight: string
        mobileWidth: string
        position: {
          top: string
          right: string
          left: string
          bottom: string
        }
        mobilePosition: {
          top: string
          right: string
          left: string
          bottom: string
        }
        text: TextStyling
        mobileText: TextStyling
        backgroundColor: Property.Color
        hoverText: TextStyling
        hoverBackgroundColor: Property.Color
      }
      font: {
        trigger: TextStyling
        bodyText: TextStyling
        alternateText: TextStyling
        cta: Font
      }
    }
  }
  navigation: {
    alignItems: Alignment
    backgroundColor: {
      active: Property.Color
      inactive: Property.Color
    }
    bottomSeparator: Separator
    borderTop: string
    borderBottom: string
    font: {
      active: TextStyling
      inactive: TextStyling
    }
    dropdownHeaderHover: {
      hoverLinkFont: TextStyling
      hoverBackgroundColor: Property.Color
    }
    linkHover: {
      hoverLinkFont: TextStyling
      hoverBackgroundColor: Property.Color
    }
    breakLine: boolean
    verticalSeparator: Separator
    tabPanelVerticalMargin: string
    height: string
    itemWidth: {
      max: string
      min: string
    }
    linkFont: TextStyling
    dropdownHeaderFont: TextStyling
    mobileLinkFont: TextStyling
    homeIconColor: Property.Color
    closeIconColor: Property.Color
    chevronIconColor: Property.Color
    burgerIconColor: Property.Color
    mobileLinkHover: TextStyling
    mobileLinkBackgroundHover: Property.Color
    waterfall: {
      link: TextStyling
      hoverLink: TextStyling
      hoverBackgroundColor: Property.Color
    }
  }
  subNavigation: {
    backgroundColor: Property.Color
    bottomSeparator: Separator
    hoverColor: Property.Color
    color: Property.Color
    dropdowns: {
      triggerHeight: string
      account: {
        icon: {
          height: string
          width: string
        }
      }
    }
    mobile: {
      font: TextStyling
      footerBackgroundColor: Property.Color
      changeTextColor: Property.Color
    }
    desktop: {
      font: TextStyling
      hoverFont: TextStyling
    }
  }
  headerLogo: {
    mobile: {
      height: string
      headerHeight: string
      order: Order
      alignment: Alignment
      useAlternateLogo: boolean
    }
    desktop: {
      height: string
      verticalMargin: string
      order: Order
      alignment: Alignment
      useAlternateLogo: boolean
    }
  }
  search: {
    width: string
    order: {
      mobile: Order
      desktop: Order
    }
    icon: IconStyling
    iconColor: { active: Property.Color; inactive: Property.Color }
    clearTextIcon: Property.Color
    border: { active: BorderBox; inactive: BorderBox; mobile: Border }
    searchBorders: { active: BorderBox; inactive: BorderBox; mobile: Border }
    buttonBackgroundColor: { active: Property.Color; inactive: Property.Color }
    buttonBorder: { active: BorderBox; inactive: BorderBox; mobile: Border }
    backgroundColor: { active: Property.Color; inactive: Property.Color }
    placeholderFont: TextStyling
    placeHolderPadding: string
    dropdownHeaderFont: TextStyling
    linkFont: TextStyling
    activeFont: TextStyling
    mobileIcon: IconStyling
    mobileCloseIconColor: Property.Color
  }
  mobileSearch: boolean
  wishlist: {
    icon: IconStyling
  }
}

const textStyling: TextStyling = {
  entry: 'bodyText',
  style: 'default',
  transform: 'none',
  textDecoration: 'none',
  textColor: '#333333',
}
const ctaTextStyling: Font = {
  entry: 'bodyText',
  style: 'default',
  transform: 'none',
  textDecoration: 'none',
}

const alternateTextStyling: TextStyling = {
  entry: 'bodyText',
  style: 'alternate',
  transform: 'none',
  textDecoration: 'none',
  textColor: '#333333',
}

const hoverTextStyling: TextStyling = {
  entry: 'bodyText',
  style: 'default',
  transform: 'none',
  textDecoration: 'none',
  textColor: '#333333',
}

const borderStyling: Border = {
  display: true,
  color: '#8C8C8C',
  width: '1px',
}

const borders: BorderBox = {
  radius: '0px',
  top: borderStyling,
  right: borderStyling,
  bottom: borderStyling,
  left: borderStyling,
}

const alternateBorderStyling: Border = {
  display: true,
  color: '#8C8C8C',
  width: '0px',
}

const alternateBorders: BorderBox = {
  radius: '0px',
  top: alternateBorderStyling,
  right: alternateBorderStyling,
  bottom: alternateBorderStyling,
  left: alternateBorderStyling,
}

const mobileSlots: SlotConfiguration[] = [
  {
    component: HeaderComponent.FLYOUT,
    colSpan: '48px',
    alignment: Alignment.center,
  },
  {
    component: HeaderComponent.SEARCH,
    colSpan: '48px',
    alignment: Alignment.center,
  },
  {
    component: HeaderComponent.LOGO,
    colSpan: 'auto',
    alignment: Alignment.center,
  },
  {
    component: HeaderComponent.ACCOUNT,
    colSpan: '48px',
    alignment: Alignment.center,
  },
  {
    component: HeaderComponent.BASKET,
    colSpan: '48px',
    alignment: Alignment.center,
  },
]
const desktopSlots: SlotConfiguration[] = [
  {
    component: HeaderComponent.LOGO,
    colSpan: 'auto',
    alignment: Alignment.flexStart,
  },
  {
    component: HeaderComponent.SEARCH,
    colSpan: 'auto',
    alignment: Alignment.center,
  },
  {
    component: HeaderComponent.ACCOUNT,
    colSpan: 'auto',
    alignment: Alignment.center,
  },
  {
    component: HeaderComponent.BASKET,
    colSpan: 'auto',
    alignment: Alignment.center,
  },
]

export const theme: HeaderThemeInterface = {
  backgroundColor: '#FFFFFF',
  slotsGridAreas: {
    xs: {
      area: `"\"flyout search logo account basket\""`,
      colSpan: '48px 48px auto 48px 48px',
    },
    sm: {
      area: `"\"flyout search logo account basket\""`,
      colSpan: '48px 48px auto 48px 48px',
    },
    md: {
      area: `"\"flyout search logo account basket\""`,
      colSpan: '48px 48px auto 48px 48px',
    },
    lg: {
      area: `"\"submenu submenu submenu submenu\" \"logo search account basket\" \"navigation navigation navigation navigation\""`,
      colSpan: 'auto auto auto auto',
    },
  },
  flyout: {
    direction: {
      lg: FlyoutDirection.LEFT,
      md: FlyoutDirection.LEFT,
      sm: FlyoutDirection.LEFT,
      xs: FlyoutDirection.LEFT,
    },
    icon: {
      svgPath: '',
      height: '24px',
      width: '24px',
      color: '#333333',
      viewBox: '0 0 24 24',
    },
  },
  enabledComponents: {
    xs: [
      HeaderComponent.FLYOUT,
      HeaderComponent.SEARCH,
      HeaderComponent.LOGO,
      HeaderComponent.ACCOUNT,
      HeaderComponent.BASKET,
    ],
    sm: [
      HeaderComponent.FLYOUT,
      HeaderComponent.SEARCH,
      HeaderComponent.LOGO,
      HeaderComponent.ACCOUNT,
      HeaderComponent.BASKET,
    ],
    md: [
      HeaderComponent.FLYOUT,
      HeaderComponent.SEARCH,
      HeaderComponent.LOGO,
      HeaderComponent.ACCOUNT,
      HeaderComponent.BASKET,
    ],
    lg: [
      HeaderComponent.SUBMENU,
      HeaderComponent.LOGO,
      HeaderComponent.SEARCH,
      HeaderComponent.ACCOUNT,
      HeaderComponent.BASKET,
      HeaderComponent.NAVIGATION,
    ],
  },
  accountDropdownDirection: {
    xs: DropdownDirection.LEFT,
    sm: DropdownDirection.LEFT,
    md: DropdownDirection.LEFT,
    lg: DropdownDirection.LEFT,
  },
  slots: [
    {
      xs: mobileSlots,
      sm: mobileSlots,
      md: mobileSlots,
      lg: [
        {
          component: HeaderComponent.SUBMENU,
          colSpan: 'auto',
          alignment: Alignment.flexEnd,
        },
      ],
    },
    {
      xs: [],
      sm: [],
      md: [],
      lg: desktopSlots,
    },
    {
      xs: [],
      sm: [],
      md: [],
      lg: [
        {
          component: HeaderComponent.NAVIGATION,
          colSpan: 'auto',
          alignment: Alignment.flexStart,
        },
      ],
    },
  ],
  headerSlots: {
    iconColor: { active: '#333333', inactive: '#333333' },
    textStyling: {
      active: textStyling,
      inactive: textStyling,
    },
    orientation: Orientation.HORIZONTAL,
  },
  slotItems: {
    submenu: [
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
    ],
    header: [
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
    ],
    flyout: [
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
      {
        visibility: {
          xs: Visibility.SHOW,
          sm: Visibility.SHOW,
          md: Visibility.SHOW,
          lg: Visibility.SHOW,
        },
      },
      {
        visibility: {
          xs: Visibility.HIDE,
          sm: Visibility.HIDE,
          md: Visibility.HIDE,
          lg: Visibility.HIDE,
        },
      },
      {
        visibility: {
          xs: Visibility.HIDE,
          sm: Visibility.HIDE,
          md: Visibility.HIDE,
          lg: Visibility.HIDE,
        },
      },
    ],
  },
  bottomSeparator: {
    color: '#333333',
    width: '0px',
  },
  search: {
    width: '458px',
    order: {
      mobile: Order.two,
      desktop: Order.two,
    },
    icon: {
      svgPath: '',
      height: '24px',
      width: '24px',
      color: '#333333',
      viewBox: '0 0 24 24',
    },
    placeHolderPadding: '8px',
    placeholderFont: textStyling,
    dropdownHeaderFont: textStyling,
    linkFont: textStyling,
    activeFont: textStyling,
    iconColor: { active: '#FFFFFF', inactive: '#333333' },
    border: { active: borders, inactive: borders, mobile: borderStyling },
    searchBorders: {
      active: borders,
      inactive: borders,
      mobile: borderStyling,
    },
    buttonBackgroundColor: { active: '#8C8C8C', inactive: '#FFFFFF' },
    buttonBorder: {
      active: alternateBorders,
      inactive: alternateBorders,
      mobile: borderStyling,
    },
    backgroundColor: { active: '#FFFFFF', inactive: '#FFFFFF' },
    clearTextIcon: '#333333',
    mobileIcon: {
      svgPath: '',
      height: '24px',
      width: '24px',
      color: '#333333',
      viewBox: '0 0 24 24',
    },
    mobileCloseIconColor: '#333333',
  },
  dropdowns: {
    order: {
      mobile: Order.four,
      desktop: Order.three,
    },
    triggerHeight: '48px',
    account: {
      hideText: false,
      width: '275px',
      icon: {
        svgPath: '',
        width: '24px',
        height: '24px',
        color: '#333333',
        viewBox: '0 0 24 24',
      },
      iconColor: '#333333',
      mobileIconColor: '#333333',
      mobileIcon: {
        svgPath: '',
        width: '24px',
        height: '24px',
        color: '#333333',
        viewBox: '0 0 24 24',
      },
      hoverIconColor: '#333333',
      closeIconColor: '#333333',
      font: {
        trigger: textStyling,
        dropdownLink: textStyling,
        ctaHigh: ctaTextStyling,
        ctaMedium: ctaTextStyling,
        welcomeText: textStyling,
      },
    },
    basket: {
      hideText: false,
      width: '320px',
      icon: {
        svgPath: '',
        width: '24px',
        height: '24px',
        color: '#333333',
        viewBox: '0 0 24 24',
      },
      mobileIcon: {
        svgPath: '',
        width: '24px',
        height: '24px',
        color: '#333333',
        viewBox: '0 0 24 24',
      },
      iconColor: '#333333',
      mobileIconColor: '#333333',
      hoverIconColor: '#333333',
      closeIconColor: '#333333',
      quantityStyling: {
        height: '16px',
        width: '16px',
        mobileHeight: '16px',
        mobileWidth: '16px',
        position: {
          top: '-5px',
          right: '-5px',
          left: '',
          bottom: '0',
        },
        mobilePosition: {
          top: '-5px',
          right: '-5px',
          left: '',
          bottom: '0',
        },
        text: {
          entry: 'xsmall',
          style: 'default',
          transform: 'none',
          textDecoration: 'none',
          textColor: '#FFFFFF',
        },
        mobileText: {
          entry: 'xsmall',
          style: 'default',
          transform: 'none',
          textDecoration: 'none',
          textColor: '#FFFFFF',
        },
        backgroundColor: '#333333',
        hoverText: {
          entry: 'xsmall',
          style: 'default',
          transform: 'none',
          textDecoration: 'none',
          textColor: '#FFFFFF',
        },
        hoverBackgroundColor: '#333333',
      },
      font: {
        trigger: textStyling,
        bodyText: textStyling,
        alternateText: alternateTextStyling,
        cta: ctaTextStyling,
      },
    },
  },
  navigation: {
    alignItems: Alignment.flexStart,
    backgroundColor: { active: '#F2F2F2', inactive: '#FFFFFF' },
    borderTop: '',
    borderBottom: '',
    bottomSeparator: {
      color: '#333333',
      width: '0px',
    },
    font: {
      active: textStyling,
      inactive: textStyling,
    },
    dropdownHeaderHover: {
      hoverLinkFont: textStyling,
      hoverBackgroundColor: '#FFFFFF',
    },
    linkHover: {
      hoverLinkFont: textStyling,
      hoverBackgroundColor: '#FFFFFF',
    },
    breakLine: false,
    verticalSeparator: { color: '#FFFFFF', width: '0px' },
    tabPanelVerticalMargin: '0px',
    height: '40px',
    itemWidth: {
      max: 'auto',
      min: 'auto',
    },
    linkFont: textStyling,
    dropdownHeaderFont: alternateTextStyling,
    mobileLinkFont: alternateTextStyling,
    mobileLinkBackgroundHover: '#F2F2F2',
    mobileLinkHover: alternateTextStyling,
    homeIconColor: '#333333',
    closeIconColor: '#333333',
    chevronIconColor: '#333333',
    burgerIconColor: '#333333',
    waterfall: {
      link: {
        entry: 'small',
        style: 'default',
        transform: 'none',
        textDecoration: 'none',
        textColor: '#333333',
      },
      hoverLink: {
        entry: 'small',
        style: 'default',
        transform: 'none',
        textDecoration: 'underline',
        textColor: '#333333',
      },
      hoverBackgroundColor: '#FFFFFF',
    },
  },
  subNavigation: {
    backgroundColor: '#F2F2F2',
    bottomSeparator: {
      color: '#333333',
      width: '0px',
    },
    hoverColor: '#CCCCCC',
    color: '#333333',
    dropdowns: {
      triggerHeight: '34px',
      account: {
        icon: {
          height: '24px',
          width: '24px',
        },
      },
    },
    mobile: {
      font: textStyling,
      footerBackgroundColor: '#F2F2F2',
      changeTextColor: '#333333',
    },
    desktop: {
      font: textStyling,
      hoverFont: hoverTextStyling,
    },
  },
  headerLogo: {
    mobile: {
      height: '15.43px',
      headerHeight: '48px',
      order: Order.three,
      alignment: Alignment.flexStart,
      useAlternateLogo: false,
    },
    desktop: {
      height: '18px',
      verticalMargin: '24px',
      order: Order.one,
      alignment: Alignment.flexStart,
      useAlternateLogo: false,
    },
  },
  mobileSearch: false,
  wishlist: {
    icon: {
      svgPath: '',
      width: '24px',
      height: '24px',
      color: '#333333',
      viewBox: '0 0 24 24',
    },
  },
}
